import { useCallback } from "react";
import { useSwivuBryntumContext } from "../useSwivuBryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { generateInsight, getInsightTypeAvatar, cancelInsightGeneration } from "@/network/network";
import { Insights } from "@/bryntum-lib/models/Insights";

export const useFileInsights = () => {
  const { project } = useSwivuBryntumContext();

  /**
   * Delete an insight by ID
   * @param id - The insight ID to delete
   */
  const deleteInsight = useCallback(
    (id: string | number) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return;
      }

      store.remove(id);
    },
    [project]
  );

  /**
   * Generate a new insight for a file
   * @param file_id - The file ID to generate insight for
   * @param insight_type - The type of insight (analyze, execute, review, site_safety, workmanship)
   * @param language - The language code (en or es, defaults to en)
   * @param signal - Optional AbortSignal to cancel the request
   * @returns Promise that resolves when insight is added to store
   */
  const generateInsightForFile = useCallback(
    async (file_id: number, insight_type: string, language: string = "en", signal?: AbortSignal) => {
      if (!project) {
        console.warn("[useFileInsights] Project not available");
        return;
      }

      try {
        const response = await generateInsight(
          {
            file_id,
            insight_type,
            language,
          },
          signal
        );

        const store = project.getCrudStore(STORE_KEYS.insightsStore);
        if (!store) {
          console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
          return;
        }

        // Add the insight to the store
        store.add(response.data);
      } catch (error) {
        console.error("[useFileInsights] Failed to generate insight:", error);
        throw error;
      }
    },
    [project]
  );

  /**
   * Get avatar URL for an insight type
   * @param insightType - The insight type (analyze, execute, review, site_safety, workmanship)
   * @returns Promise that resolves with avatar_url
   */
  const getInsightTypesAvatar = useCallback(async (insightType: string): Promise<string | null> => {
    try {
      const response = await getInsightTypeAvatar(insightType);
      return response.data?.avatar_url || null;
    } catch (error) {
      console.error(`[useFileInsights] Failed to get avatar for insight type "${insightType}":`, error);
      return null;
    }
  }, []);

  /**
   * Get all insights for a specific file
   * @param fileId - The file ID to get insights for
   * @returns Array of insights for the file
   */
  const getInsights = useCallback(
    (fileId: number | string | undefined): Insights[] => {
      if (!project || !fileId) {
        return [];
      }

      const store = project.getCrudStore(STORE_KEYS.insightsStore);
      if (!store) {
        console.warn(`[useFileInsights] Store with id "${STORE_KEYS.insightsStore}" not found`);
        return [];
      }

      return store.query((insight: Insights) => insight.file === fileId) as Insights[];
    },
    [project]
  );

  /**
   * Cancel insight generation for a file
   * @param file_id - The file ID
   * @param insight_type - The type of insight being cancelled
   * @returns Promise that resolves when cancellation is complete
   */
  const cancelInsight = useCallback(
    async (file_id: number, insight_type: string) => {
      try {
        await cancelInsightGeneration({
          file_id,
          insight_type,
        });
        console.log(`[useFileInsights] Cancelled insight generation for file ${file_id}, type ${insight_type}`);
      } catch (error) {
        console.error("[useFileInsights] Failed to cancel insight generation:", error);
      }
    },
    []
  );

  return {
    deleteInsight,
    generateInsight: generateInsightForFile,
    getInsightTypesAvatar,
    getInsights,
    cancelInsight,
  };
};
